import { BaseChart, useDefaultLineChartOptions } from '@talos/kyoko';
import type {
  AxisLabelsFormatterContextObject,
  Options,
  SeriesLineOptions,
  TooltipFormatterContextObject,
} from 'highcharts';
import { useMemo, useRef } from 'react';
import { useTheme } from 'styled-components';

interface SignalsDualAxisChartProps {
  primarySeries: SeriesLineOptions[];
  secondarySeries: SeriesLineOptions[];
  primaryAxisLabel: string;
  secondaryAxisLabel: string;
  /** Formats LHS tick labels and tooltip values for primary series. */
  formatPrimaryValue: (v: number) => string;
  /** Formats RHS tick labels and tooltip values for secondary series. */
  formatSecondaryValue: (v: number) => string;
}

const CHART_WRAPPER_PROPS = { h: '100%', w: '100%' } as const;

/** Left/right plot margins reserved for the y-axis titles + tick values. Exported so overlays
 * (e.g. a legend) can align to the plot area rather than covering the axis labels. */
export const SIGNALS_DUAL_AXIS_MARGIN_LEFT = 78;
export const SIGNALS_DUAL_AXIS_MARGIN_RIGHT = 84;

function defaultFormat(v: number): string {
  return v.toLocaleString();
}

/** Shared dual-axis line chart for Signals modules that need two y-axes (e.g. % and USD). */
export function SignalsDualAxisChart({
  primarySeries,
  secondarySeries,
  primaryAxisLabel,
  secondaryAxisLabel,
  formatPrimaryValue = defaultFormat,
  formatSecondaryValue = defaultFormat,
}: SignalsDualAxisChartProps) {
  const theme = useTheme();
  const lineChartDefaults = useDefaultLineChartOptions(theme);

  const formatPrimaryRef = useRef(formatPrimaryValue);
  formatPrimaryRef.current = formatPrimaryValue;
  const formatSecondaryRef = useRef(formatSecondaryValue);
  formatSecondaryRef.current = formatSecondaryValue;

  // Tag secondary series with yAxis: 1 so Highcharts plots them on the RHS axis.
  // A stable `id` (derived from the series name) makes Highcharts match series by identity on
  // update instead of by index — without it, removing a series (e.g. toggling MA off) merges the
  // next series onto the removed slot and leaks its style (a dashed line lingering on the price).
  const allSeries = useMemo<SeriesLineOptions[]>(
    () => [
      ...primarySeries.map(s => ({ ...s, id: s.id ?? s.name })),
      ...secondarySeries.map(s => ({ ...s, id: s.id ?? s.name, yAxis: 1 })),
    ],
    [primarySeries, secondarySeries]
  );

  const options = useMemo<Options>(
    () => ({
      ...lineChartDefaults,
      chart: {
        ...lineChartDefaults.chart,
        marginLeft: SIGNALS_DUAL_AXIS_MARGIN_LEFT,
        marginRight: SIGNALS_DUAL_AXIS_MARGIN_RIGHT,
        marginBottom: 15,
        zooming: { type: 'x', mouseWheel: { enabled: true } },
      },
      xAxis: {
        ...lineChartDefaults.xAxis,
        crosshair: false,
        labels: {
          style: {
            color: theme.colorTextDefault,
            fontSize: `${theme.fontSizeTiny}rem`,
          },
        },
      },
      // Defined as an array here — bypasses the deepmerge object-vs-array conflict that
      // occurs when using LineChart's internal merge (default yAxis is a plain object).
      yAxis: [
        {
          opposite: false,
          title: {
            text: primaryAxisLabel,
            style: { color: theme.colorTextSubtle, fontSize: `${theme.fontSizeTiny}rem` },
          },
          gridLineColor: theme.borderColorChartGridLine,
          labels: {
            align: 'right',
            x: -8,
            formatter(this: AxisLabelsFormatterContextObject): string {
              return formatPrimaryRef.current(this.value as number);
            },
            style: { color: theme.colorTextDefault, fontSize: `${theme.fontSizeTiny}rem` },
          },
        },
        {
          opposite: true,
          title: {
            text: secondaryAxisLabel,
            style: { color: theme.colorTextSubtle, fontSize: `${theme.fontSizeTiny}rem` },
          },
          gridLineColor: theme.borderColorChartGridLine,
          labels: {
            align: 'left',
            x: 8,
            formatter(this: AxisLabelsFormatterContextObject): string {
              return formatSecondaryRef.current(this.value as number);
            },
            style: { color: theme.colorTextDefault, fontSize: `${theme.fontSizeTiny}rem` },
          },
        },
      ],
      series: allSeries,
      // We render a custom overlay legend; disable Highcharts' native one to avoid a duplicate.
      legend: { enabled: false },
      plotOptions: {
        ...lineChartDefaults.plotOptions,
        line: {
          ...lineChartDefaults.plotOptions?.line,
          marker: { enabled: false, states: { hover: { enabled: true } } },
          // Disable turbo: past its 1000-point threshold (reached at 1H), a series removed then
          // re-added by id (toggling an exchange) fails to rebuild its path and silently vanishes.
          // Series are bounded, so rendering every point costs little.
          turboThreshold: 0,
        },
      },
      tooltip: {
        shared: true,
        split: false,
        // Render at document-body level (high z-index) so the overlay legend can't cover it.
        outside: true,
        formatter(this: TooltipFormatterContextObject): string {
          const points = (this as any).points as any[] | undefined;
          if (!points?.length) {
            return '';
          }
          const date = new Date(this.x as number).toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          });
          const rows = points.map(p => {
            const isSecondary = p.series.userOptions.yAxis === 1;
            const formatted = isSecondary
              ? formatSecondaryRef.current(p.y as number)
              : formatPrimaryRef.current(p.y as number);
            return `<span style="color:${p.color}">●</span> ${p.series.name}: <b>${formatted}</b>`;
          });
          return `<b>${date}</b><br/>${rows.join('<br/>')}`;
        },
      },
    }),
    [
      lineChartDefaults,
      theme.colorTextDefault,
      theme.colorTextSubtle,
      theme.borderColorChartGridLine,
      theme.fontSizeTiny,
      primaryAxisLabel,
      secondaryAxisLabel,
      allSeries,
    ]
  );

  return (
    <BaseChart options={options} constructorType="stockChart" sizeAware showNoData wrapperProps={CHART_WRAPPER_PROPS} />
  );
}
